import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import UserProgressContext from "../../userProgressContext";
import BackgroundSoundToggle from "../BackgroundSound/backGroundSoundToggle";

const DIFFICULTIES = [
  {
    id: "beginner",
    label: "Beginner",
    tagline: "Story-first walkthrough",
    bullets: [
      "All narrative, dialogue, and quizzes preserved",
      "Each security challenge is explained through a guided expert walkthrough",
      "Earn points for every scenario you complete",
      "~25 min playthrough",
    ],
    accentClass: "border-cyan-500/70 hover:border-cyan-400",
    selectedClass: "ring-2 ring-cyan-400 border-cyan-400 bg-cyan-900/25",
    tagClass: "bg-cyan-900/30 text-cyan-400 border-cyan-500/40",
    checkColor: "text-cyan-400",
  },
  {
    id: "advanced",
    label: "Advanced",
    tagline: "Full simulation",
    bullets: [
      "All 6 interactive minigames active",
      "Hands-on terminals, drag-drop forensics, isometric triage",
      "Higher maximum score",
      "~60 min playthrough",
    ],
    accentClass: "border-purple-500/70 hover:border-purple-400",
    selectedClass: "ring-2 ring-purple-400 border-purple-400 bg-purple-900/25",
    tagClass: "bg-purple-900/30 text-purple-300 border-purple-500/40",
    checkColor: "text-purple-400",
  },
];

const DifficultySelect = () => {
  const { name, difficulty, setDifficulty } = useContext(UserProgressContext);
  const navigate = useNavigate();
  const location = useLocation();

  const pendingRoute = location.state?.pendingRoute || "/level/0";

  const [selected, setSelected] = useState(
    () => difficulty || localStorage.getItem("lastDifficulty") || "beginner"
  );

  useEffect(() => {
    if (!name || name.length === 0) navigate("/");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = () => {
    if (!selected) return;
    setDifficulty(selected);
    localStorage.setItem("lastDifficulty", selected);
    navigate(pendingRoute, { replace: true });
  };

  return (
    <div className="w-full min-h-screen z-10 flex flex-col items-center justify-center p-6 md:p-12 bg-cyber-bg/85 text-cyber-text">
      <h1 className="text-2xl md:text-4xl font-bold mb-3 text-center tracking-widest uppercase text-cyber-accent">
        Select Your Difficulty
      </h1>
      <p className="text-cyber-sub text-sm md:text-base mb-10 text-center tracking-wide">
        Choose how you want to experience the adventure. You can always start over to change it.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl w-full mb-10">
        {DIFFICULTIES.map((d) => {
          const isSelected = selected === d.id;
          return (
            <button
              key={d.id}
              onClick={() => setSelected(d.id)}
              className={`text-left bg-cyber-panel/60 border rounded-lg p-6 cursor-pointer transition-all duration-200 ${d.accentClass} ${isSelected ? d.selectedClass : "border-cyber-muted"}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs font-bold tracking-widest uppercase border px-2 py-0.5 rounded ${d.tagClass}`}
                  >
                    {d.label}
                  </span>
                  <span className="text-cyber-faint text-sm">{d.tagline}</span>
                </div>
                {isSelected && (
                  <span className={`text-lg font-bold ${d.checkColor}`}>✓</span>
                )}
              </div>
              <ul className="space-y-2">
                {d.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-cyber-text">
                    <span className="mt-0.5 text-cyber-accent/60">▸</span>
                    {b}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <button
        onClick={handleConfirm}
        className="bg-cyber-panel hover:bg-cyber-muted border border-cyber-accent text-cyber-accent font-bold py-2 px-10 rounded tracking-widest uppercase transition-colors duration-200"
      >
        Confirm &amp; Start
      </button>

      <div className="absolute top-2 md:top-4 right-4">
        <BackgroundSoundToggle />
      </div>
    </div>
  );
};

export default DifficultySelect;
